<script setup>
import {computed, nextTick, onMounted, ref, useId, watch} from 'vue'
import {useNotificationStore} from '@/stores/NotificationStore.js'
import tagOptionLabel from './_optionLabel.vue'

// Drop-in replacement for <tags-multi-detach>: same props, events and slots.
const props = defineProps({
    selectedLeads: {
        type: Array,
        required: true,
    },
    showTopbar: {
        type: Boolean,
        required: false,
        default: true,
    },
})

const emit = defineEmits(['close', 'doReloadLeads'])

const notificationStore = useNotificationStore()
const listboxId = useId()

const searchInput = ref(null)

const availableTags = ref([])
const loadingTags = ref(true)
const working = ref(false)
const search = ref('')
const selectedTagId = ref(null)
const highlightedIndex = ref(-1)

const loadTags = async () => {
    loadingTags.value = true
    try {
        const response = await axios.get(route('crm.leadtags.index'))
        availableTags.value = response.data.tags
    } catch (error) {
        console.error('Error loading tags:', error)
    } finally {
        loadingTags.value = false
    }

    await nextTick()
    searchInput.value?.focus()
}

const searchTerm = computed(() => search.value.trim().toLowerCase())

// System tags are managed by the platform and can't be removed by hand
const detachableTags = computed(() => (
    availableTags.value.filter((tag) => ! tag.is_system_tag)
))

const filteredTags = computed(() => {
    if (! searchTerm.value) {
        return detachableTags.value
    }

    return detachableTags.value.filter((tag) => tag.name.toLowerCase().includes(searchTerm.value))
})

const optionId = (index) => `${listboxId}-option-${index}`

const canDetach = computed(() => selectedTagId.value !== null && props.selectedLeads.length > 0)

const detachTag = async () => {
    if (! canDetach.value || working.value) {
        return
    }

    working.value = true
    try {
        const response = await axios.post(route('crm.detachTags', selectedTagId.value), {
            selected: props.selectedLeads,
        })

        search.value = ''
        selectedTagId.value = null

        notificationStore.addNotification({
            type: 'success',
            title: response.data.tag.name,
            message: _mft('crm:tag.removed'),
            dismissAfter: 2500,
        })

        emit('doReloadLeads')
    } catch (error) {
        notificationStore.addNotification({
            type: 'error',
            message: _mft('error:error.generalOccurred'),
            dismissAfter: 2500,
        })
    } finally {
        working.value = false
        searchInput.value?.focus()
    }
}

const chooseTag = (tag) => {
    selectedTagId.value = selectedTagId.value === tag.id ? null : tag.id
}

const activateHighlighted = () => {
    const index = highlightedIndex.value
    if (index < 0 || index >= filteredTags.value.length) {
        // Nothing highlighted: Enter confirms the current selection
        detachTag()
        return
    }

    chooseTag(filteredTags.value[index])
}

const moveHighlight = (step) => {
    const optionCount = filteredTags.value.length
    if (optionCount === 0) {
        return
    }

    const start = highlightedIndex.value === -1 && step < 0 ? 0 : highlightedIndex.value
    highlightedIndex.value = (start + step + optionCount) % optionCount
}

const onSearchKeydown = (e) => {
    switch (e.key) {
        case 'ArrowDown':
            e.preventDefault()
            moveHighlight(1)
            break
        case 'ArrowUp':
            e.preventDefault()
            moveHighlight(-1)
            break
        case 'Enter':
            e.preventDefault()
            activateHighlighted()
            break
        case 'Escape':
            // Only swallow Escape when it clears the search, so a surrounding
            // slideout or modal still gets to close on the next press
            if (search.value !== '') {
                e.stopPropagation()
                search.value = ''
            }
            break
    }
}

const close = () => {
    emit('close')
}

watch(search, () => {
    highlightedIndex.value = searchTerm.value && filteredTags.value.length > 0 ? 0 : -1
})

watch(highlightedIndex, (index) => {
    if (index < 0) {
        return
    }
    nextTick(() => {
        document.getElementById(optionId(index))?.scrollIntoView({block: 'nearest'})
    })
})

onMounted(() => {
    loadTags()
})
</script>

<template>
    <div>
        <div
            v-if="showTopbar"
            class="flex items-center justify-between gap-2 mb-3"
        >
            <span class="text-sm text-gray-500">
                {{ _mft('crm:tag.leadsSelected', {count: selectedLeads.length}) }}
            </span>
            <button-base
                size="sm"
                style-type="secondary"
                @click="close"
            >
                {{ _mft('shared:cancel') }}
            </button-base>
        </div>

        <div class="relative">
            <i class="absolute text-sm text-gray-400 -translate-y-1/2 pointer-events-none fa-regular fa-magnifying-glass left-3 top-1/2"></i>
            <input
                ref="searchInput"
                v-model="search"
                type="text"
                role="combobox"
                autocomplete="off"
                data-1p-ignore="true"
                maxlength="24"
                class="block w-full py-2 text-gray-900 bg-white border border-gray-300 rounded-md pl-9 pr-9 min-h-[2.625rem] transition-colors hover:border-gray-400 focus:border-myflowPurple focus:ring-1 focus:ring-myflowPurple/20 focus:outline-none placeholder:text-gray-400 disabled:bg-gray-50"
                :placeholder="_mft('crm:tag.searchTagsToRemove')"
                :disabled="loadingTags"
                :aria-expanded="true"
                :aria-controls="listboxId"
                :aria-activedescendant="highlightedIndex >= 0 ? optionId(highlightedIndex) : null"
                @keydown="onSearchKeydown"
            />
            <button
                v-if="search"
                type="button"
                class="absolute flex items-center justify-center text-gray-400 -translate-y-1/2 rounded-md right-1.5 top-1/2 w-7 h-7 hover:text-gray-600 hover:bg-gray-100"
                :aria-label="_mft('shared:action.clear')"
                @click="search = ''; searchInput?.focus()"
            >
                <i class="fa-solid fa-circle-xmark"></i>
            </button>
        </div>

        <div
            class="relative mt-2 overflow-hidden bg-white border border-gray-200 rounded-xl"
            :class="working ? 'pointer-events-none' : ''"
        >
            <ul
                :id="listboxId"
                role="listbox"
                class="py-1 overflow-y-auto transition-opacity max-h-72"
                :class="working ? 'opacity-40' : ''"
            >
                <template v-if="loadingTags">
                    <li
                        v-for="n in 4"
                        :key="n"
                        class="flex items-center gap-3 px-3 py-2.5"
                    >
                        <span class="w-2.5 h-2.5 bg-gray-200 rounded-full animate-pulse shrink-0"></span>
                        <span
                            class="h-3 bg-gray-200 rounded animate-pulse"
                            :class="['w-32', 'w-24', 'w-40', 'w-28'][n - 1]"
                        ></span>
                    </li>
                </template>

                <template v-else>
                    <li
                        v-for="(tag, index) in filteredTags"
                        :id="optionId(index)"
                        :key="tag.id"
                        role="option"
                        :aria-selected="selectedTagId === tag.id"
                        class="flex items-center gap-3 px-3 py-2 cursor-pointer"
                        :class="[
                            index === highlightedIndex ? 'bg-gray-100' : '',
                            selectedTagId === tag.id ? 'bg-myflowLavender-light' : '',
                        ]"
                        @mousedown.prevent
                        @mouseenter="highlightedIndex = index"
                        @click="chooseTag(tag)"
                    >
                        <tag-option-label
                            :tag="tag"
                            :emphasized="selectedTagId === tag.id"
                        />

                        <i
                            v-if="selectedTagId === tag.id"
                            class="text-sm fa-solid fa-check text-myflowPurple shrink-0"
                        ></i>
                    </li>

                    <li
                        v-if="filteredTags.length === 0"
                        class="px-3 py-2 text-sm text-gray-500"
                    >
                        {{ _mft('crm:tag.noTagsFound') }}
                    </li>
                </template>
            </ul>

            <div
                v-if="working"
                class="absolute inset-0 flex items-center justify-center"
            >
                <mf-spinner-medium class="w-12 h-6"></mf-spinner-medium>
            </div>
        </div>

        <div class="flex items-center justify-end gap-2 mt-3">
            <slot name="actions"></slot>
            <button-base
                style-type="danger"
                :disabled="! canDetach"
                :state="working ? 'waiting' : ''"
                @click="detachTag"
            >
                <span v-if="canDetach">
                    {{ _mft('crm:tag.removeFromNLeads', {count: selectedLeads.length}) }}
                </span>
                <span v-else>{{ _mft('crm:tag.selectTagToRemove') }}</span>
            </button-base>
        </div>
    </div>
</template>

<style>

</style>
